import { useEffect, useRef } from 'react';

/**
 * A custom hook that sets up a timeout.
 * @see https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 *
 * @param callback The function to be called after the timeout.
 * @param delay The delay in milliseconds before the timeout triggers. If null, the timeout is canceled.
 */
export const useTimeout = (callback: () => void, delay: number | null) => {
  if (callback === undefined || delay === undefined) {
    throw new Error('Callback and delay must be provided to useTimeout');
  }

  const savedCallback = useRef<() => void>(callback);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the timeout
  useEffect(() => {
    const tick = () => {
      savedCallback.current();
    };

    if (delay !== null) {
      const timeoutId = setTimeout(tick, delay);
      return () => clearTimeout(timeoutId);
    }
  }, [delay]);
};
